import type { GatewayStatusData } from "./api-client.js";

export type GatewayEventsSseState = "connecting" | "connected" | "failed";

export interface GatewayStatusMessageEvent {
  data: string;
}

export interface GatewayEventsSource {
  onopen: (() => void) | null;
  onerror: (() => void) | null;
  addEventListener(
    event: "gateway.status",
    listener: (event: GatewayStatusMessageEvent) => void,
  ): void;
  removeEventListener(
    event: "gateway.status",
    listener: (event: GatewayStatusMessageEvent) => void,
  ): void;
  close(): void;
}

export interface GatewayEventsSseOptions {
  eventsUrl: string;
  createEventSource(url: string, init: { withCredentials: true }): GatewayEventsSource;
  onState(state: GatewayEventsSseState): void;
  onStatus(status: GatewayStatusData): void;
  terminalFailureThreshold?: number;
  initialRetryDelayMs?: number;
  maxRetryDelayMs?: number;
  setTimeoutFn?: (callback: () => void, delayMs: number) => unknown;
  clearTimeoutFn?: (handle: unknown) => void;
}

export function startGatewayEventsSse(options: GatewayEventsSseOptions): () => void {
  const controller = new GatewayEventsSseController(options);
  controller.start();
  return () => controller.stop();
}

class GatewayEventsSseController {
  private source: GatewayEventsSource | undefined;
  private stopped = false;
  private failures = 0;
  private retryDelayMs: number;
  private retryTimer: unknown;

  constructor(private readonly options: GatewayEventsSseOptions) {
    this.retryDelayMs = options.initialRetryDelayMs ?? 60_000;
  }

  start(): void {
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.clearRetry();
    this.closeSource();
  }

  private connect(): void {
    if (this.stopped) return;
    this.clearRetry();
    this.closeSource();
    this.options.onState("connecting");

    const source = this.options.createEventSource(this.options.eventsUrl, {
      withCredentials: true,
    });
    this.source = source;
    source.onopen = () => {
      this.failures = 0;
      this.retryDelayMs = this.options.initialRetryDelayMs ?? 60_000;
      this.options.onState("connected");
    };
    source.onerror = () => {
      this.failures += 1;
      this.options.onState("connecting");
      if (this.failures >= (this.options.terminalFailureThreshold ?? 3)) {
        this.closeSource();
        this.scheduleRetry();
      }
    };
    source.addEventListener("gateway.status", this.handleStatus);
  }

  private readonly handleStatus = (event: GatewayStatusMessageEvent) => {
    const status = parseGatewayStatusEvent(event.data);
    if (!status) return;
    this.failures = 0;
    this.retryDelayMs = this.options.initialRetryDelayMs ?? 60_000;
    this.options.onState("connected");
    this.options.onStatus(status);
  };

  private scheduleRetry(): void {
    if (this.stopped || this.retryTimer !== undefined) return;
    const delay = this.retryDelayMs;
    this.retryDelayMs = Math.min(delay * 2, this.options.maxRetryDelayMs ?? 5 * 60_000);
    this.retryTimer = (this.options.setTimeoutFn ?? setTimeout)(() => {
      this.retryTimer = undefined;
      this.failures = 0;
      this.connect();
    }, delay);
  }

  private clearRetry(): void {
    if (this.retryTimer === undefined) return;
    (this.options.clearTimeoutFn ?? clearTimeout)(this.retryTimer as ReturnType<typeof setTimeout>);
    this.retryTimer = undefined;
  }

  private closeSource(): void {
    if (!this.source) return;
    this.source.removeEventListener("gateway.status", this.handleStatus);
    this.source.close();
    this.source = undefined;
  }
}

export function parseGatewayStatusEvent(data: string): GatewayStatusData | undefined {
  try {
    const value = JSON.parse(data) as Partial<GatewayStatusData>;
    if (
      typeof value.gatewayId === "string" &&
      (value.status === "online" || value.status === "offline")
    ) {
      return value as GatewayStatusData;
    }
  } catch {
    return undefined;
  }
  return undefined;
}
